import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CompanyService } from '@app/shared/services/company.service';
import { TranslocoService } from '@jsverse/transloco';
import type { Translation } from '@jsverse/transloco';
import { Transition, TransitionService } from '@uirouter/angular';
let deregisterSetTitleHook: Function | null = null;

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private defaultTitle = '';
  private unreadCount = 0;
  private currentTitle = '';

  constructor(
    private $transitions: TransitionService,
    private companyService: CompanyService,
    private title: Title,
    private translocoService: TranslocoService,
    private liveAnnouncer: LiveAnnouncer
  ) {}

  private get suffix() {
    return this.companyService.data?.name ? `| ${this.companyService.data?.name}` : '';
  }

  init() {
    this.defaultTitle = this.title.getTitle();

    deregisterSetTitleHook?.();
    deregisterSetTitleHook = this.$transitions.onSuccess({}, (transition: Transition) => {
      const to = transition.to();
      const title = to.data?.title;
      const ignoreStateAsTitle = to.data?.ignoreStateAsTitle;

      if (!ignoreStateAsTitle && !title) {
        this.setTitleFromStateName(to.name as string);
      } else if (title) {
        this.setTitle(title);
      }
    });
  }

  // Sets title from translation key
  setTitle(key: string, params?: Translation) {
    this.translocoService.selectTranslate(key, params).subscribe(translation => {
      const translatedTitle = translation !== '' && key !== translation ? translation : this.defaultTitle;

      // use default title when there is no translation for key
      this.changeDocumentTitle(`${translatedTitle} ${this.suffix}`);
      this.announceNavigationChange(translatedTitle);
    });
  }

  // Sets title from raw text
  setTitleText(text: string) {
    this.changeDocumentTitle(`${text} ${this.suffix}`);
    this.announceNavigationChange(text);
  }

  setTitleFromStateName(stateName: string) {
    this.setTitle(`page-titles.${stateName}`);
  }

  setUnreadNotificationsCount(count: number) {
    this.unreadCount = count;
    this.changeDocumentTitle(this.currentTitle, false);
  }

  private changeDocumentTitle(title: string, saveCurrentTitle = true) {
    if (saveCurrentTitle) {
      this.currentTitle = title;
    }

    if (this.unreadCount > 0) {
      this.title.setTitle(`(${this.unreadCount}) ${title}`);
    } else {
      this.title.setTitle(title);
    }
  }

  private announceNavigationChange(message: string) {
    if (!message) {
      return;
    }

    void this.liveAnnouncer.announce(message, 'polite');
  }
}
